/**
 * Phase 2.5: Procedure Confirmation Panel
 * 
 * Simple checklist derived from authorized CPT subset.
 * Replaces downstream billing clarification emails.
 */

import { CheckCircle2, XCircle, HelpCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ProcedureConfirmation, ConfirmationStatus } from '@/lib/dictation/types';

interface ProcedureConfirmationPanelProps {
  confirmations: ProcedureConfirmation[];
  onUpdate: (cptCode: string, status: ConfirmationStatus) => void;
  disabled?: boolean;
}

const STATUS_CONFIG: Record<ConfirmationStatus, {
  label: string;
  icon: typeof CheckCircle2;
  className: string;
  activeClassName: string;
}> = {
  confirmed_performed: {
    label: 'Confirmed Performed',
    icon: CheckCircle2,
    className: 'border-success/30 text-success/60 hover:bg-success/10',
    activeClassName: 'bg-success/20 border-success text-success',
  },
  not_performed: {
    label: 'Not Performed',
    icon: XCircle,
    className: 'border-destructive/30 text-destructive/60 hover:bg-destructive/10',
    activeClassName: 'bg-destructive/20 border-destructive text-destructive',
  },
  needs_review: {
    label: 'Needs Review',
    icon: HelpCircle,
    className: 'border-warning/30 text-warning/60 hover:bg-warning/10',
    activeClassName: 'bg-warning/20 border-warning text-warning',
  },
  pending: {
    label: 'Pending',
    icon: Clock,
    className: 'border-border text-muted-foreground',
    activeClassName: '',
  },
};

export function ProcedureConfirmationPanel({
  confirmations,
  onUpdate,
  disabled = false,
}: ProcedureConfirmationPanelProps) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Procedure Confirmation</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Confirm each procedure from the authorized CPT bundle. This replaces downstream billing clarification.
        </p>
      </div>

      <div className="space-y-3">
        {confirmations.map(conf => (
          <div
            key={conf.cptCode}
            className="border border-border/50 rounded-lg p-4 space-y-3"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-sm font-semibold text-primary">
                  {conf.cptCode}
                </p>
                <p className="text-sm text-foreground/80 mt-0.5">
                  {conf.description}
                </p>
              </div>
              {conf.status !== 'pending' && (
                <div className={cn(
                  'px-2 py-1 rounded text-xs font-medium',
                  STATUS_CONFIG[conf.status].activeClassName
                )}>
                  {STATUS_CONFIG[conf.status].label}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              {(['confirmed_performed', 'not_performed', 'needs_review'] as ConfirmationStatus[]).map(status => {
                const config = STATUS_CONFIG[status];
                const Icon = config.icon;
                const isActive = conf.status === status;
                return (
                  <Button
                    key={status}
                    variant="outline"
                    size="sm"
                    disabled={disabled}
                    className={cn(
                      'flex-1 gap-1.5 text-xs',
                      isActive ? config.activeClassName : config.className
                    )}
                    onClick={() => onUpdate(conf.cptCode, status)}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {config.label}
                  </Button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
